// FeedbackPrompt — drop this at an EARNED moment (post-completion, a win — never a
// low moment). It pulls the Signal Engine brain (/feedback/active); if there's an
// eligible ask for this student, it renders a dismissible card with FeedbackCapture.
// Renders nothing when there's no ask, so it's safe to mount anywhere.
// See docs/feedback_engine_design.md.

import React, { useEffect, useRef, useState } from 'react';
import feedbackAPI, { ActiveResponse } from '../../services/feedbackAPI';
import FeedbackCapture from './FeedbackCapture';
import { useTheme } from '../../contexts/ThemeContext';

interface Props {
  surface?: string;
  trigger?: string;
  /** Gate so callers only fire on a genuine earned moment (e.g. set completed). */
  enabled?: boolean;
}

const FeedbackPrompt: React.FC<Props> = ({ surface = 'web_inflow', trigger, enabled = true }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [active, setActive] = useState<ActiveResponse | null>(null);
  const [closed, setClosed] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || fetchedRef.current) return;
    fetchedRef.current = true;
    feedbackAPI
      .getActive(surface, trigger)
      .then((res) => {
        if (res.campaign) {
          setActive(res);
          if (res.request_id) feedbackAPI.markOpen(res.request_id);
        }
      })
      .catch(() => {
        /* no ask / offline — stay silent */
      });
  }, [enabled, surface, trigger]);

  if (!active || !active.campaign || closed) return null;

  const dismiss = () => {
    if (active.request_id) feedbackAPI.markDismiss(active.request_id);
    setClosed(true);
  };

  return (
    <div
      data-testid="feedback-prompt"
      style={{
        background: dark ? '#171b21' : '#fff',
        border: dark ? '1.5px solid #f5c563' : '1.5px solid #f5b301',
        borderRadius: 14,
        padding: '14px 16px',
        marginTop: 14,
        boxShadow: dark ? '0 2px 10px rgba(0,0,0,0.4)' : '0 2px 10px rgba(245,179,1,0.12)',
        position: 'relative',
      }}
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: 'absolute', top: 8, right: 10, border: 'none', background: 'none',
          fontSize: 18, color: dark ? '#7d8590' : '#94a3b8', cursor: 'pointer', lineHeight: 1,
        }}
      >
        ×
      </button>
      <FeedbackCapture
        campaign={active.campaign}
        requestId={active.request_id}
        surface={surface}
        onDone={() => setClosed(true)}
      />
    </div>
  );
};

export default FeedbackPrompt;
